import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useConstants } from '@/contexts/ConstantsContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CreateLeadModal = ({ open, onOpenChange, onSuccess }) => {
  const { user } = useAuth();
  const { industries, country_markets, icp_fits, lead_sources, next_step_types, company_sizes, priorities, preferred_phones, best_times_to_call, users, sectors, getNichesForSector } = useConstants();

  const [formData, setFormData] = useState({
    full_name: '',
    company_name: '',
    phone: '',
    owner_id: user?.id || '',
    country_market: '',
    industry: '',
    sector: '',
    niche: '',
    icp_fit: '',
    lead_source: '',
    next_step_type: 'Discovery',
    email: '',
    linkedin_url: '',
    website: '',
    city_state: '',
    job_title: '',
    company_size: '',
    source_detail: '',
    priority: 'Medium',
    deal_value_estimate: '',
    notes: '',
    preferred_phone: 'Unknown',
    best_time_to_call: 'Unknown',
    use_case: '',
    current_tools: '',
  });
  
  // Get niches for selected sector
  const availableNiches = formData.sector ? getNichesForSector(formData.sector) : [];
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      // Reset niche when sector changes
      if (field === 'sector') {
        newData.niche = '';
      }
      return newData;
    });
  };

  const handleSubmit = async () => {
    // Validate required fields
    const required = ['full_name', 'company_name', 'phone', 'country_market', 'industry', 'icp_fit', 'lead_source'];
    const missing = required.filter(f => !formData[f]);
    if (missing.length > 0) {
      toast.error(`Please fill in: ${missing.join(', ')}`);
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...formData,
        deal_value_estimate: formData.deal_value_estimate ? parseFloat(formData.deal_value_estimate) : null,
      };
      await axios.post(`${API}/leads`, data);
      toast.success('Lead created successfully');
      setFormData({
        full_name: '',
        company_name: '',
        phone: '',
        owner_id: user?.id || '',
        country_market: '',
        industry: '',
        sector: '',
        niche: '',
        icp_fit: '',
        lead_source: '',
        next_step_type: 'Discovery',
        email: '',
        linkedin_url: '',
        website: '',
        city_state: '',
        job_title: '',
        company_size: '',
        source_detail: '',
        priority: 'Medium',
        deal_value_estimate: '',
        notes: '',
        preferred_phone: 'Unknown',
        best_time_to_call: 'Unknown',
        use_case: '',
        current_tools: '',
      });
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create lead');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl bg-card border-border max-h-[90vh]" data-testid="create-lead-modal">
        <DialogHeader>
          <DialogTitle>Create New Lead</DialogTitle>
        </DialogHeader>

        <ScrollArea className="h-[calc(90vh-120px)] pr-4">
          <div className="space-y-6 py-4">
            {/* Required Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Required Information</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Full Name *</Label>
                  <Input
                    value={formData.full_name}
                    onChange={(e) => handleChange('full_name', e.target.value)}
                    placeholder="John Smith"
                    className="bg-secondary border-border"
                    data-testid="lead-full-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Company Name *</Label>
                  <Input
                    value={formData.company_name}
                    onChange={(e) => handleChange('company_name', e.target.value)}
                    placeholder="Acme Inc"
                    className="bg-secondary border-border"
                    data-testid="lead-company-name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Phone *</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="+44 7700 900123"
                    className="bg-secondary border-border"
                    data-testid="lead-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Owner *</Label>
                  <Select value={formData.owner_id} onValueChange={(v) => handleChange('owner_id', v)}>
                    <SelectTrigger className="bg-secondary border-border" data-testid="lead-owner">
                      <SelectValue placeholder="Select owner" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Market *</Label>
                  <Select value={formData.country_market} onValueChange={(v) => handleChange('country_market', v)}>
                    <SelectTrigger className="bg-secondary border-border" data-testid="lead-market">
                      <SelectValue placeholder="Select market" />
                    </SelectTrigger>
                    <SelectContent>
                      {country_markets.map(m => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Industry *</Label>
                  <Select value={formData.industry} onValueChange={(v) => handleChange('industry', v)}>
                    <SelectTrigger className="bg-secondary border-border" data-testid="lead-industry">
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {industries.map(i => (
                        <SelectItem key={i} value={i}>{i}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Sector</Label>
                  <Select value={formData.sector || "none"} onValueChange={(v) => handleChange('sector', v === "none" ? "" : v)}>
                    <SelectTrigger className="bg-secondary border-border" data-testid="lead-sector">
                      <SelectValue placeholder="Select sector" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not specified</SelectItem>
                      {sectors.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Niche</Label>
                  <Select 
                    value={formData.niche || "none"} 
                    onValueChange={(v) => handleChange('niche', v === "none" ? "" : v)}
                    disabled={!formData.sector}
                  >
                    <SelectTrigger className="bg-secondary border-border" data-testid="lead-niche">
                      <SelectValue placeholder={formData.sector ? "Select niche" : "Select sector first"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not specified</SelectItem>
                      {availableNiches.map(n => (
                        <SelectItem key={n} value={n}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>ICP Fit *</Label>
                  <Select value={formData.icp_fit} onValueChange={(v) => handleChange('icp_fit', v)}>
                    <SelectTrigger className="bg-secondary border-border" data-testid="lead-icp">
                      <SelectValue placeholder="Select fit" />
                    </SelectTrigger>
                    <SelectContent>
                      {icp_fits.map(f => (
                        <SelectItem key={f} value={f}>{f}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Lead Source *</Label>
                  <Select value={formData.lead_source} onValueChange={(v) => handleChange('lead_source', v)}>
                    <SelectTrigger className="bg-secondary border-border" data-testid="lead-source">
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent>
                      {lead_sources.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Optional Section */}
            <div className="space-y-4 pt-4 border-t border-border">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Optional Information</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Job Title</Label>
                  <Input
                    value={formData.job_title}
                    onChange={(e) => handleChange('job_title', e.target.value)}
                    placeholder="CEO, Director, etc."
                    className="bg-secondary border-border"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="john@acme.com"
                    className="bg-secondary border-border"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>LinkedIn URL</Label>
                  <Input
                    value={formData.linkedin_url}
                    onChange={(e) => handleChange('linkedin_url', e.target.value)}
                    placeholder="https://linkedin.com/in/..."
                    className="bg-secondary border-border"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Website</Label>
                  <Input
                    value={formData.website}
                    onChange={(e) => handleChange('website', e.target.value)}
                    placeholder="https://acme.com"
                    className="bg-secondary border-border"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>City/State</Label>
                  <Input
                    value={formData.city_state}
                    onChange={(e) => handleChange('city_state', e.target.value)}
                    placeholder="London, Manchester..."
                    className="bg-secondary border-border"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Company Size</Label>
                  <Select value={formData.company_size || "none"} onValueChange={(v) => handleChange('company_size', v === "none" ? "" : v)}>
                    <SelectTrigger className="bg-secondary border-border">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not specified</SelectItem>
                      {company_sizes.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Source Detail</Label>
                  <Input
                    value={formData.source_detail}
                    onChange={(e) => handleChange('source_detail', e.target.value)}
                    placeholder="Maps search: plumbers Manchester"
                    className="bg-secondary border-border"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Deal Value Estimate</Label>
                  <Input
                    type="number"
                    value={formData.deal_value_estimate}
                    onChange={(e) => handleChange('deal_value_estimate', e.target.value)}
                    placeholder="50000"
                    className="bg-secondary border-border"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select value={formData.priority} onValueChange={(v) => handleChange('priority', v)}>
                    <SelectTrigger className="bg-secondary border-border">
                      <SelectValue placeholder="Select priority" />
                    </SelectTrigger>
                    <SelectContent>
                      {priorities.map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Next Step Type</Label>
                  <Select value={formData.next_step_type} onValueChange={(v) => handleChange('next_step_type', v)}>
                    <SelectTrigger className="bg-secondary border-border">
                      <SelectValue placeholder="Select next step" />
                    </SelectTrigger>
                    <SelectContent>
                      {next_step_types.map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Preferred Phone</Label>
                  <Select value={formData.preferred_phone} onValueChange={(v) => handleChange('preferred_phone', v)}>
                    <SelectTrigger className="bg-secondary border-border">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {preferred_phones.map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Best Time to Call</Label>
                  <Select value={formData.best_time_to_call} onValueChange={(v) => handleChange('best_time_to_call', v)}>
                    <SelectTrigger className="bg-secondary border-border">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {best_times_to_call.map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* AI Context */}
              <div className="space-y-4 pt-4 border-t border-border">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">AI Context</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Use Case</Label>
                    <Input
                      value={formData.use_case}
                      onChange={(e) => handleChange('use_case', e.target.value)}
                      placeholder="Customer service automation"
                      className="bg-secondary border-border"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Current Tools</Label>
                    <Input
                      value={formData.current_tools}
                      onChange={(e) => handleChange('current_tools', e.target.value)}
                      placeholder="Zendesk, HubSpot"
                      className="bg-secondary border-border"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  placeholder="Any additional notes..."
                  className="bg-secondary border-border resize-none"
                  rows={3}
                />
              </div>
            </div>

            <Button
              className="w-full"
              onClick={handleSubmit}
              disabled={saving}
              data-testid="submit-create-lead"
            >
              {saving ? 'Creating...' : 'Create Lead'}
            </Button>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default CreateLeadModal;
